import { useCallback } from 'react';

import { isZigzag } from '@common/device-manager';
import { useIsomorphicLayoutEffect } from '@common/hooks';
import { Log, useUBL } from '@common/log-manager';

import { createConnectionTypeLabel } from '../utils/network-connection';
import { ATFElementSelector, calculateATFByElements, calculateATFByTags } from './atf-core';
import { getFirstContentfulPaintTime } from '../first-contentful-paint/first-contentful-paint-core';

interface ATFMetricsParams {
  payload: Omit<Log, 'category'>;
  options?: {
    enabled?: boolean | (() => boolean);
  };
}

const ATF_CONTENT_LOAD_END_LABEL = 'web_atf_content_load_end';

/**
 페이지 최상단 컴포넌트에서만 사용 (페이지마다 최초 1회 실행용)

 ```
 import { useATFMetricsPage } from '@common/metrics';

 useATFMetricsPage({
   payload: {
     navigation: 'content_detail',
   },
   options: {
     tags: ['img', 'video'], // default: ['img', 'video']
     duration: { // optional
      min: 0, // default: 0
      max: 20000, // default: 20000
     },
     enabled: true, // default: true
   }
 });
 ```
 */
export const useATFMetricsPage = (
  params: ATFMetricsParams & {
    options?: {
      tags?: Array<ATFElementSelector>;
      duration?: {
        min?: number;
        max?: number;
      };
    };
  },
) => {
  const { tags = ['img', 'video'], enabled = true } = params.options ?? {};
  // NOTE)
  // 230908: [TTI 고도화][P0] Bi-weekly 코어미팅 - 4초 이상은 이상 수치로 판단한다.
  const { min = 0, max = 5000 } = params?.options?.duration ?? {};

  const sendUBL = useUBL();

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    if (typeof enabled === 'function' && enabled() === false) {
      return;
    }

    const sendATFMetricsLog = async () => {
      try {
        const atf = await calculateATFByTags(tags);
        const duration = atf.highResTimestamp;

        if (duration === 0) {
          return;
        }

        if (duration < min) {
          return;
        }

        if (max < duration) {
          return;
        }

        // NOTE: wifi, cellular 여부
        const connectionType = createConnectionTypeLabel();

        if (params.payload.navigation_sub?.page_url) {
          const pageUrl = new URL(params.payload.navigation_sub.page_url);
          const externalSearchParams = new URLSearchParams(window.location.search);

          externalSearchParams.forEach((value, name) => {
            pageUrl.searchParams.set(name, value);
          });

          params.payload.navigation_sub.page_url = pageUrl.href;
        }

        const fcp = getFirstContentfulPaintTime();
        const metrics = fcp ? { fcp } : {};

        sendUBL({
          ...params.payload,
          category: 'tti',
          data: {
            platform_type: isZigzag() ? 'App' : 'Web',
            connection_type: connectionType,
            metrics,
            ...params.payload?.data,
            tti: {
              category: 'scene_tti',
              label: ATF_CONTENT_LOAD_END_LABEL,
              ...params.payload?.data?.tti,
              timestamp: atf.timestamp,
              duration,
            },
          },
        });
      } catch (e) {
        console.warn(e);
      }
    };

    if (document.readyState === 'complete') {
      const timeout = setTimeout(sendATFMetricsLog, 1000);
      const visibilityChangeListener = () => {
        if (document.visibilityState !== 'hidden') {
          return;
        }

        clearTimeout(timeout);
      };

      document.addEventListener('visibilitychange', visibilityChangeListener);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', visibilityChangeListener);
      };
    }

    const visibilityChangeListener = () => {
      if (document.visibilityState !== 'hidden') {
        return;
      }

      window.removeEventListener('load', sendATFMetricsLog);
    };

    window.addEventListener('load', sendATFMetricsLog);
    document.addEventListener('visibilitychange', visibilityChangeListener);
    return () => {
      window.removeEventListener('load', sendATFMetricsLog);
      document.removeEventListener('visibilitychange', visibilityChangeListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};

/**
 사용 예시)
 --
 ```
 import { useATFMetricsPage } from '@common/metrics';
 
 const { sendATFMetricsLog, getATFMetrics } = useATFMetrics();

 image1.onload = (e) => {
   const element = e.target;

   sendATFMetricsLog({
     elements: [element],
     payload: {
       navigation: 'content_detail',
     },
     options: {
       delay: 0, // default: 0
     }
   });
 }

 const sendUBL = useUBL();

 image2.onload = async (e) => {
   const element = e.target;
   const atf = await getATFMetrics([element]);

   console.log(atf.timestamp, atf.highResTimestamp);

   sendUBL({
    ...
   });
 }
 ```
 */
export const useATFMetrics = () => {
  const sendUBL = useUBL();

  const sendATFMetricsLog = useCallback(
    (
      params: Omit<ATFMetricsParams, 'enabled'> & {
        elements: Element[] | HTMLCollectionOf<HTMLElement>;
      },
    ) => {
      const { elements = [] } = params ?? {};

      const timeout = setTimeout(async () => {
        try {
          const atf = await calculateATFByElements(elements);
          // NOTE: wifi, cellular 여부
          const connectionType = createConnectionTypeLabel();

          sendUBL({
            ...params.payload,
            category: 'tti',
            data: {
              platform_type: isZigzag() ? 'App' : 'Web',
              connection_type: connectionType,
              ...params.payload?.data,
              tti: {
                category: 'scene_tti',
                label: ATF_CONTENT_LOAD_END_LABEL,
                status: '200',
                ...params.payload?.data?.tti,
                timestamp: atf.timestamp,
                duration: atf.highResTimestamp,
              },
            },
          });
        } catch (e) {
          console.warn(e);
        }
      });

      return () => {
        clearTimeout(timeout);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    getATFMetrics: calculateATFByElements,
    sendATFMetricsLog,
  };
};
